import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, ArrowLeft, ArrowRight, Mail, CheckCircle, Shield } from 'lucide-react';
import { authAPI } from '../lib/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await authAPI.forgotPassword(email);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="login-container" data-testid="forgot-password-page">
      {/* Left - Image */}
      <div
        className="login-left"
        style={{ backgroundImage: `url(https://images.unsplash.com/photo-1765062165737-ed4b47014fef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc3MDgxODIzMXww&ixlib=rb-4.1.0&q=85)` }}
      >
        <div className="relative z-10 flex flex-col justify-end h-full p-12">
          <h2
            className="text-white text-5xl font-bold leading-tight mb-4"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            PASSWORD<br />RECOVERY
          </h2>
          <p className="text-slate-300 text-base max-w-md leading-relaxed">
            Enter your email address and we'll send you a secure link to reset your password.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="login-right">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, hsl(221, 83%, 53%), hsl(221, 83%, 42%))", boxShadow: "0 4px 14px rgba(37, 99, 235, 0.25)" }}>
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                SIMLABDT
              </h1>
              <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Manager</span>
            </div>
          </div>

          {!success ? (
            <>
              <Link 
                to="/login"
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-blue-700 mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back to login
              </Link>

              <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                Forgot Password?
              </h3>
              <p className="text-slate-500 text-sm mb-8">
                No worries, we'll send you reset instructions.
              </p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-6 text-sm" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="email" className="text-slate-700 text-xs uppercase tracking-wider font-bold mb-2 block">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="rounded-lg border-slate-300 focus:border-blue-500 focus:ring-blue-500/20 bg-slate-50 focus:bg-white h-11"
                    data-testid="forgot-email-input"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary rounded-lg h-11 font-medium shadow-sm active:scale-[0.98] transition-transform"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                  {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-emerald-600" />
              </div>
              
              <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                Check Your Email
              </h3>
              <p className="text-slate-500 text-sm mb-8">
                We've sent a password reset link to:
              </p>
              
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-slate-700">{email}</p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> The reset link will expire in <strong>1 hour</strong>. 
                  If you don't see the email, check your spam folder.
                </p>
              </div>

              <Link to="/login">
                <Button className="w-full btn-primary rounded-lg h-11" data-testid="back-to-login-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
                </Button>
              </Link>

              <p className="mt-4 text-xs text-slate-400">
                Didn't receive the email?{' '}
                <button 
                  onClick={() => { setSuccess(false); setEmail(''); }}
                  className="text-blue-700 hover:underline"
                  data-testid="try-again-btn"
                >
                  Try again
                </button>
              </p>
            </div>
          )}

          <div className="mt-8 flex items-center gap-2 text-[11px] text-slate-400">
            <Shield className="w-3.5 h-3.5" />
            <span>Secured password reset &middot; Link expires in 1 hour</span>
          </div>
        </div>
      </div>
    </div>
  );
}
